import React, { useState, useRef, useEffect } from 'react';
import { FiSave, FiTrash2, FiPlus, FiEdit2, FiEye, FiEyeOff, FiCircle, FiSquare, FiLayers, FiFileText, FiSend } from 'react-icons/fi';
import './AnnotationToolStyles.css';
import { Rnd } from 'react-rnd';
import { useTranslation } from 'react-i18next';
import Api from '../../services/Api';
import { FaWhatsapp } from 'react-icons/fa';
import Functions from '../../components/Functions';

/**
 * Ferramenta de anotação iridológica para profissionais
 * Permite marcar áreas na íris e associar informações de análise
 */
const AnnotationTool = ({ business, call, user, images, clientData, onSaveAnnotation, onSendEmail }) => {
  const { t } = useTranslation();

  // Novo processamento para o formato de images
  // images: { irisLeft: { images: [...] }, irisRight: { images: [...] } }
  const processedImages = React.useMemo(() => {
    if (!images) return [];
    var urlFiles = Api.URL_FILES_BUSINESS + '/' + business._id + '/' + call._id;
    const left = images.irisLeft?.images?.map(img => ({
      src: urlFiles + '/' + encodeURI(img),
      filename: img,
      eye: 'L'
    })) || [];
    const right = images.irisRight?.images?.map(img => ({
      src: urlFiles + '/' + encodeURI(img),
      filename: img,
      eye: 'R'
    })) || [];
    return [...left, ...right];
  }, [images]);

  // Estado para controlar a imagem atual sendo analisada
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Estado para armazenar as anotações
  const [annotations, setAnnotations] = useState([]);

  // Estado para a anotação que está sendo editada atualmente
  const [currentAnnotation, setCurrentAnnotation] = useState(null);

  // Estado para controlar o modo de desenho (ponto, polígono, etc)
  const [drawingMode, setDrawingMode] = useState('polygon'); // 'polygon', 'point', 'none'

  // Estado para armazenar os pontos do polígono atual sendo desenhado
  const [currentPolygon, setCurrentPolygon] = useState([]);

  // Estado para controlar a visibilidade das anotações
  const [showAnnotations, setShowAnnotations] = useState(true);

  // Estado para controlar a visibilidade do mapa de íris
  const [showIrisMap, setShowIrisMap] = useState(false);

  // Estado para controlar a opacidade do mapa de íris
  const [irisMapOpacity, setIrisMapOpacity] = useState(0.8);

  // Estado para controlar a inversão de cores do mapa de íris
  const [invertIrisMap, setInvertIrisMap] = useState(false);

  // Estado para o formulário de detalhes da anotação
  const [annotationDetails, setAnnotationDetails] = useState({
    sign_id: '',
    type: '',
    iridology_zone: '',
    characteristics: {
      color: '',
      size: '',
      depth: '',
      shape: ''
    },
    expert_interpretation: '',
    iridological_meaning: '',
    interpretation: '',
    related_bodies: '',
    potential_gravity: '',
    recommendations: '',
    clinical_recommendations: ''
  });

  // Estado para armazenar a análise geral da íris
  const [overallAnalysis, setOverallAnalysis] = useState({
    overall_constitution: '',
    fibers: '',
    general_observations: '',
    recommendations: ''
  });

  // Estado para armazenar a qualidade da foto para cada imagem
  const [imageQualities, setImageQualities] = useState([]);

  // Estado para armazenar a cor da íris para cada imagem
  const [irisColors, setIrisColors] = useState([]);

  // Referência para o canvas de anotação
  const canvasRef = useRef(null);

  // Referência para o contêiner da imagem
  const imageContainerRef = useRef(null);

  // Referência para a imagem
  const imageRef = useRef(null);

  // Referência para o mapa de íris
  const irisMapRef = useRef(null);

  // Opções para qualidade da foto organizadas por categoria
  const imageQualityOptionsByCategory = {
    'Qualidades Positivas': [
      { value: 'Excelente', label: 'Excelente' },
      { value: 'Boa', label: 'Boa' }
    ],
    'Qualidades Negativas': [
      { value: 'Regular', label: 'Regular' },
      { value: 'Ruim', label: 'Ruim' },
      { value: 'Péssima', label: 'Péssima' }
    ]
  };

  // Opções para qualidade da foto (lista completa)
  const imageQualityOptions = [
    { value: 'Excelente', label: 'Excelente' },
    { value: 'Boa', label: 'Boa' },
    { value: 'Regular', label: 'Regular' },
    { value: 'Ruim', label: 'Ruim' },
    { value: 'Péssima', label: 'Péssima' }
  ];

  // Opções para identificador do sinal organizadas por tipo
  const signIdOptionsByType = {
    'Lacuna': [
      { value: 'lacuna_digestiva', label: 'Lacuna Digestiva' },
      { value: 'lacuna_nervosa', label: 'Lacuna Nervosa' },
      { value: 'lacuna_circulatoria', label: 'Lacuna Circulatória' },
      { value: 'lacuna_linfatica', label: 'Lacuna Linfática' }
    ],
    'Pigmento': [
      { value: 'pigmento_marrom', label: 'Pigmento Marrom' },
      { value: 'pigmento_laranja', label: 'Pigmento Laranja' },
      { value: 'pigmento_amarelo', label: 'Pigmento Amarelo' }
    ],
    'Anel': [
      { value: 'anel_nervoso', label: 'Anel Nervoso (Anel Ansiedade)' },
      { value: 'anel_digestivo', label: 'Anel Digestivo (Colorete)' },
      { value: 'anel_linfatico', label: 'Anel Linfático' },
      { value: 'anel_de_contracao ', label: 'Anel de Contração (Anel de Esteria)' },
      { value: 'anel_de_autoprotecao', label: 'Anel de Autoproteção (Anel Psíquico)' },
      { value: 'anel_gastrico', label: 'Anel Gástrico' },
      { value: 'anel_senil', label: 'Anel da Velhice (Anel Senil)' }
    ],
    'Arco': [
      { value: 'arco_lipemico', label: 'Arco Lipêmico (Anel Lipêmico)' }
    ],
    'Cripta': [
      { value: 'cripta_aguda', label: 'Cripta Aguda (Fissura Agura)' },
      { value: 'cripta_cronica', label: 'Cripta Crônica (Fissura Crônica)' }
    ],
    'Mancha': [
      { value: 'mancha_toxica', label: 'Mancha Tóxica' }
    ],
    'Raio Solar': [
      { value: 'raio_solar_digestivo', label: 'Raio Solar Digestivo' },
      { value: 'raio_solar_nervoso', label: 'Raio Solar Nervoso' },
      { value: 'raio_solar_circulatorio', label: 'Raio Solar Circulatório' }
    ],
    'Outro': [
      { value: 'outro', label: 'Outro' }
    ]
  };

  // Opções para identificador do sinal (mantida para compatibilidade)
  const signIdOptions = [
    { value: 'lacuna_digestiva', label: 'Lacuna Digestiva' },
    { value: 'lacuna_nervosa', label: 'Lacuna Nervosa' },
    { value: 'lacuna_circulatoria', label: 'Lacuna Circulatória' },
    { value: 'lacuna_linfatica', label: 'Lacuna Linfática' },
    { value: 'pigmento_marrom', label: 'Pigmento Marrom' },
    { value: 'pigmento_laranja', label: 'Pigmento Laranja' },
    { value: 'pigmento_amarelo', label: 'Pigmento Amarelo' },
    { value: 'anel_nervoso', label: 'Anel Nervoso (Anel Ansiedade)' },
    { value: 'anel_digestivo', label: 'Anel Digestivo' },
    { value: 'anel_linfatico', label: 'Anel Linfático' },
    { value: 'anel_de_contracao ', label: 'Anel de Contração (Anel de Esteria)' },
    { value: 'anel_de_autoprotecao', label: 'Anel de Autoproteção (Anel Psíquico)' },
    { value: 'anel_gastrico', label: 'Anel Gástrico' },
    { value: 'anel_senil', label: 'Anel da Velhice (Anel Senil)' },
    { value: 'arco_lipemico', label: 'Arco Lipêmico (Anel Lipêmico)' },
    { value: 'cripta_aguda', label: 'Cripta Aguda (Fissura Agura)' },
    { value: 'cripta_cronica', label: 'Cripta Crônica (Fissura Crônica)' },
    { value: 'mancha_toxica', label: 'Mancha Tóxica' },
    { value: 'outro', label: 'Outro' }
  ];

  // Opções para cor da íris organizadas por categoria
  const irisColorOptionsByCategory = {
    'Cores Básicas': [
      { value: 'Azul', label: 'Azul' },
      { value: 'Verde', label: 'Verde' },
      { value: 'Castanho Claro', label: 'Castanho Claro' },
      { value: 'Castanho Médio', label: 'Castanho Médio' },
      { value: 'Castanho Escuro', label: 'Castanho Escuro' }
    ],
    'Cores Especiais': [
      { value: 'Preto', label: 'Preto' },
      { value: 'Âmbar', label: 'Âmbar' },
      { value: 'Avelã', label: 'Avelã' },
      { value: 'Heterocromia', label: 'Heterocromia' }
    ]
  };

  // Opções para cor da íris (lista completa)
  const irisColorOptions = [
    { value: 'Azul', label: 'Azul' },
    { value: 'Verde', label: 'Verde' },
    { value: 'Castanho Claro', label: 'Castanho Claro' },
    { value: 'Castanho Médio', label: 'Castanho Médio' },
    { value: 'Castanho Escuro', label: 'Castanho Escuro' },
    { value: 'Preto', label: 'Preto' },
    { value: 'Âmbar', label: 'Âmbar' },
    { value: 'Avelã', label: 'Avelã' },
    { value: 'Heterocromia', label: 'Heterocromia' }
  ];

  // Opções para zona iridológica
  const iridologyZoneOptions = [
    { value: 'Zona Cerebral', label: 'Zona Cerebral' },
    { value: 'Zona Digestiva', label: 'Zona Digestiva' },
    { value: 'Zona Respiratória', label: 'Zona Respiratória' },
    { value: 'Zona Circulatória', label: 'Zona Circulatória' },
    { value: 'Zona Linfática', label: 'Zona Linfática' },
    { value: 'Zona Urinária', label: 'Zona Urinária' },
    { value: 'Zona Reprodutiva', label: 'Zona Reprodutiva' },
    { value: 'Zona Muscular', label: 'Zona Muscular' },
    { value: 'Zona Esquelética', label: 'Zona Esquelética' },
    { value: 'Zona Nervosa', label: 'Zona Nervosa' },
    { value: 'Zona Sensorial', label: 'Zona Sensorial' },
    { value: 'Zona Endócrina', label: 'Zona Endócrina' },
    { value: 'Zona Hepática', label: 'Zona Hepática' },
    { value: 'Zona Intestinal', label: 'Zona Intestinal' },
    { value: 'Zona Renal', label: 'Zona Renal' },
    { value: 'Zona Cardíaca', label: 'Zona Cardíaca' },
    { value: 'Zona Pulmonar', label: 'Zona Pulmonar' },
    { value: 'Zona Pele', label: 'Zona da Pele' },
    { value: 'Outra', label: 'Outra' }
  ];

  // Opções para cor dos sinais
  const signColorOptions = [
    { value: 'Branco', label: 'Branco' },
    { value: 'Amarelo', label: 'Amarelo' },
    { value: 'Laranja', label: 'Laranja' },
    { value: 'Vermelho', label: 'Vermelho' },
    { value: 'Marrom', label: 'Marrom' },
    { value: 'Preto', label: 'Preto' },
    { value: 'Azul', label: 'Azul' },
    { value: 'Verde', label: 'Verde' },
    { value: 'Cinza', label: 'Cinza' }
  ];

  // Opções para tamanho dos sinais (lista completa)
  const signSizeOptions = [
    { value: 'Pequeno', label: 'Pequeno' },
    { value: 'Médio', label: 'Médio' },
    { value: 'Grande', label: 'Grande' }
  ];

  // Opções para profundidade dos sinais (lista completa)
  const signDepthOptions = [
    { value: 'Superficial', label: 'Superficial' },
    { value: 'Média', label: 'Média' },
    { value: 'Profunda', label: 'Profunda' }
  ];

  // Opções para forma dos sinais (lista completa)
  const signShapeOptions = [
    { value: 'Circular', label: 'Circular' },
    { value: 'Oval', label: 'Oval' },
    { value: 'Linear', label: 'Linear' },
    { value: 'Radial', label: 'Radial' },
    { value: 'Irregular', label: 'Irregular' },
    { value: 'Triangular', label: 'Triangular' },
    { value: 'Retangular', label: 'Retangular' },
    { value: 'Pontual', label: 'Pontual' },
    { value: 'Anular', label: 'Anular' }
  ];

  // Opções para constituição geral (lista completa)
  const constitutionOptions = [
    { value: 'Linfática', label: 'Linfática' },
    { value: 'Hematogênica', label: 'Hematogênica' },
    { value: 'Mista', label: 'Mista' },
    { value: 'Biliar', label: 'Biliar' },
    { value: 'Neurogênica', label: 'Neurogênica' }
  ];

  // Opções para a constituição das fibras
  const constitutionFibersOptions = [
    { value: 'Abertas', label: 'Fibras Abertas' },
    { value: 'Densas', label: 'Fibras Densas' },
    { value: 'Outra', label: 'Outra' },
  ];

  // Inicializar a qualidade da foto e cor da íris para cada imagem
  useEffect(() => {
    if (processedImages && processedImages.length > 0) {
      // Inicializar com valores padrão
      const qualities = processedImages.map(() => 'Boa');
      const colors = processedImages.map(() => '');

      setImageQualities(qualities);
      setIrisColors(colors);
    }
  }, [processedImages]);

  // Carregar anotações e dados salvos, se existirem
  useEffect(() => {
    if (
      call?.annotations &&
      processedImages.length > 0
    ) {
      // Mapear filename para índice em processedImages
      const filenameToIndex = {};
      processedImages.forEach((img, idx) => {
        filenameToIndex[img.filename] = idx;
      });

      const qualities = processedImages.map(() => 'Boa');
      const colors = processedImages.map(() => '');
      let loadedAnnotations = [];

      // Função auxiliar para processar imagens de um grupo de olhos
      const processEyeImages = (eyeImages) => {
        if (!eyeImages || !Array.isArray(eyeImages)) return;

        eyeImages.forEach((img) => {
          const imageIndex = filenameToIndex[img.image_filename];

          if (imageIndex !== undefined) {
            // Atualizar qualidade e cor
            qualities[imageIndex] = img.image_quality || 'Boa';
            colors[imageIndex] = img.iris_color || '';

            // Processar sinais
            if (Array.isArray(img.iridological_signs)) {
              img.iridological_signs.forEach((sign, signIdx) => {
                let points = [];
                if (sign.location_polygon) {
                  points = sign.location_polygon.map(([x, y]) => ({ x, y }));
                } else if (sign.location_point) {
                  points = [{ x: sign.location_point[0], y: sign.location_point[1] }];
                }

                loadedAnnotations.push({
                  id: sign.sign_id || `annotation_${imageIndex}_${signIdx}`,
                  type: sign.location_polygon ? 'polygon' : 'point',
                  imageIndex: imageIndex,
                  points,
                  details: {
                    sign_id: sign.sign_id || '',
                    type: sign.type || '',
                    iridology_zone: sign.iridology_zone || '',
                    characteristics: sign.characteristics || {
                      color: '', size: '', depth: '', shape: ''
                    },
                    expert_interpretation: sign.expert_interpretation || '',

                    // Novos campos do JSON
                    iridological_meaning: sign.iridological_meaning || '',
                    interpretation: sign.interpretation || '',
                    related_bodies: sign.related_bodies || '',
                    potential_gravity: sign.potential_gravity || '',
                    recommendations: sign.recommendations || '',
                    clinical_recommendations: sign.clinical_recommendations || ''
                  }
                });
              });
            }
          }
        });
      };

      // Verificar estrutura e processar
      if (call.annotations.eye_groups) {
        // Nova estrutura
        if (call.annotations.eye_groups.L && call.annotations.eye_groups.L.images) {
          processEyeImages(call.annotations.eye_groups.L.images);
        }
        if (call.annotations.eye_groups.R && call.annotations.eye_groups.R.images) {
          processEyeImages(call.annotations.eye_groups.R.images);
        }
      } else if (call.annotations.images) {
        // Estrutura antiga (fallback)
        processEyeImages(call.annotations.images);
      }

      setImageQualities(qualities);
      setIrisColors(colors);
      setAnnotations(loadedAnnotations);

      // Preencher overallAnalysis
      if (call.annotations.overall_analysis) {
        setOverallAnalysis({
          overall_constitution: call.annotations.overall_analysis.overall_constitution || '',
          fibers: call.annotations.overall_analysis.fibers || '',
          general_observations: call.annotations.overall_analysis.general_observations || '',
          recommendations: call.annotations.overall_analysis.recommendations || ''
        });
      }
    }
  }, [call, processedImages]);

  // Efeito para redimensionar o canvas quando a imagem carrega
  useEffect(() => {
    if (imageRef.current && canvasRef.current) {
      const updateCanvasSize = () => {
        const { width, height } = imageRef.current.getBoundingClientRect();
        canvasRef.current.width = width;
        canvasRef.current.height = height;
        drawAnnotations();
      };

      imageRef.current.onload = updateCanvasSize;

      // Também atualizar quando a janela for redimensionada
      window.addEventListener('resize', updateCanvasSize);

      return () => {
        window.removeEventListener('resize', updateCanvasSize);
      };
    }
  }, [currentImageIndex, processedImages]);

  // Efeito para desenhar as anotações quando elas mudam
  useEffect(() => {
    drawAnnotations();
  }, [annotations, showAnnotations, currentImageIndex]);

  // Função para desenhar as anotações no canvas
  const drawAnnotations = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!showAnnotations) return;

    // Filtrar anotações para a imagem atual
    const currentImageAnnotations = annotations.filter(
      ann => ann.imageIndex === currentImageIndex
    );

    // Desenhar cada anotação
    currentImageAnnotations.forEach((annotation, index) => {
      ctx.beginPath();

      // Definir estilo
      ctx.strokeStyle = annotation === currentAnnotation ? '#ff0000' : '#ff0000';
      ctx.lineWidth = 2;

      if (annotation.type === 'polygon' && annotation.points.length > 0) {
        // Desenhar polígono
        ctx.moveTo(annotation.points[0].x, annotation.points[0].y);

        for (let i = 1; i < annotation.points.length; i++) {
          ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
        }

        ctx.closePath();
        ctx.stroke();

        // Preencher com cor semi-transparente
        ctx.fillStyle = annotation === currentAnnotation
          ? 'rgba(255, 0, 0, 0.3)'
          : 'rgba(255, 0, 0, 0.2)';
        ctx.fill();
      } else if (annotation.type === 'point') {
        // Desenhar ponto
        ctx.arc(annotation.points[0].x, annotation.points[0].y, 5, 0, Math.PI * 2);
        ctx.fillStyle = annotation === currentAnnotation ? '#ff0000' : '#ff0000';
        ctx.fill();
      }

      // Adicionar número da anotação
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.font = '12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Calcular o centro do polígono ou usar o ponto
      let centerX, centerY;

      if (annotation.type === 'polygon') {
        centerX = annotation.points.reduce((sum, p) => sum + p.x, 0) / annotation.points.length;
        centerY = annotation.points.reduce((sum, p) => sum + p.y, 0) / annotation.points.length;
      } else {
        centerX = annotation.points[0].x;
        centerY = annotation.points[0].y;
      }

      ctx.strokeText((index + 1).toString(), centerX, centerY);
      ctx.fillText((index + 1).toString(), centerX, centerY);
    });

    // Desenhar o polígono atual sendo criado
    if (drawingMode === 'polygon' && currentPolygon.length > 0) {
      ctx.beginPath();
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2;

      ctx.moveTo(currentPolygon[0].x, currentPolygon[0].y);

      for (let i = 1; i < currentPolygon.length; i++) {
        ctx.lineTo(currentPolygon[i].x, currentPolygon[i].y);
      }

      ctx.stroke();
    }
  };

  // Função para lidar com cliques no canvas
  const handleCanvasClick = (e) => {
    if (drawingMode === 'none') return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (drawingMode === 'point') {
      // Criar uma nova anotação de ponto
      const newAnnotation = {
        id: `annotation_${Date.now()}`,
        type: 'point',
        imageIndex: currentImageIndex,
        points: [{ x, y }],
        details: { ...annotationDetails }
      };

      setAnnotations([...annotations, newAnnotation]);
      setCurrentAnnotation(newAnnotation);
      setDrawingMode('none');
    } else if (drawingMode === 'polygon') {
      // Adicionar ponto ao polígono atual
      setCurrentPolygon([...currentPolygon, { x, y }]);
    }
  };

  // Função para finalizar o desenho do polígono
  const finishPolygon = () => {
    if (currentPolygon.length < 3) {
      alert('Um polígono precisa ter pelo menos 3 pontos.');
      return;
    }

    // Criar uma nova anotação de polígono
    const newAnnotation = {
      id: `annotation_${Date.now()}`,
      type: 'polygon',
      imageIndex: currentImageIndex,
      points: [...currentPolygon],
      details: { ...annotationDetails }
    };

    setAnnotations([...annotations, newAnnotation]);
    setCurrentAnnotation(newAnnotation);
    setCurrentPolygon([]);
    setDrawingMode('none');
  };

  // Função para cancelar o desenho atual
  const cancelDrawing = () => {
    setCurrentPolygon([]);
    setDrawingMode('none');
  };

  // Função para selecionar uma anotação existente
  const selectAnnotation = (annotation) => {
    setCurrentAnnotation(annotation);
    setAnnotationDetails(annotation.details || { ...annotationDetails });
  };

  // Função para atualizar os detalhes da anotação atual
  const updateAnnotationDetails = (e) => {
    const { name, value } = e.target;

    if (name.startsWith('characteristics.')) {
      const characteristicName = name.split('.')[1];
      setAnnotationDetails({
        ...annotationDetails,
        characteristics: {
          ...annotationDetails.characteristics,
          [characteristicName]: value
        }
      });
    } else {
      // Se o tipo de sinal foi alterado, limpar o identificador do sinal
      if (name === 'type') {
        setAnnotationDetails({
          ...annotationDetails,
          [name]: value,
          sign_id: '' // Limpar o identificador quando o tipo muda
        });
      } else {
        setAnnotationDetails({
          ...annotationDetails,
          [name]: value
        });
      }
    }
  };

  // Função para salvar os detalhes da anotação atual
  const saveAnnotationDetails = () => {
    if (!currentAnnotation) return;

    const updatedAnnotations = annotations.map(ann =>
      ann.id === currentAnnotation.id
        ? { ...ann, details: { ...annotationDetails } }
        : ann
    );

    setAnnotations(updatedAnnotations);
    setCurrentAnnotation(null);
    setAnnotationDetails({
      sign_id: '',
      type: '',
      iridology_zone: '',
      characteristics: {
        color: '',
        size: '',
        depth: '',
        shape: ''
      },
      expert_interpretation: '',
      iridological_meaning: '',
      interpretation: '',
      related_bodies: '',
      potential_gravity: '',
      recommendations: '',
      clinical_recommendations: ''
    });
  };

  // Função para excluir a anotação atual
  const deleteAnnotation = () => {
    if (!currentAnnotation) return;

    const updatedAnnotations = annotations.filter(
      ann => ann.id !== currentAnnotation.id
    );

    setAnnotations(updatedAnnotations);
    setCurrentAnnotation(null);
    setAnnotationDetails({
      sign_id: '',
      type: '',
      iridology_zone: '',
      characteristics: {
        color: '',
        size: '',
        depth: '',
        shape: ''
      },
      expert_interpretation: '',
      iridological_meaning: '',
      interpretation: '',
      related_bodies: '',
      potential_gravity: '',
      recommendations: '',
      clinical_recommendations: ''
    });
  };

  // Função para atualizar a análise geral
  const updateOverallAnalysis = (e) => {
    const { name, value } = e.target;
    setOverallAnalysis({
      ...overallAnalysis,
      [name]: value
    });
  };

  // Função para atualizar a qualidade da foto
  const updateImageQuality = (value) => {
    const newQualities = [...imageQualities];
    newQualities[currentImageIndex] = value;
    setImageQualities(newQualities);
  };

  // Função para atualizar a cor da íris
  const updateIrisColor = (value) => {
    const newColors = [...irisColors];
    newColors[currentImageIndex] = value;
    setIrisColors(newColors);
  };

  // Função para alternar a visibilidade do mapa de íris
  const toggleIrisMap = () => {
    setShowIrisMap(!showIrisMap);
  };

  // Função para ajustar a opacidade do mapa de íris
  const handleOpacityChange = (e) => {
    setIrisMapOpacity(parseFloat(e.target.value));
  };

  const onSendWhatsApp = (clientData) => {
     window.open(Functions.sendWhatsApp(clientData.phone, ''), "_Blank");
  };

  // Função para salvar toda a análise
  const saveAnnotation = () => {
    // Verificar se há anotações
    if (annotations.length === 0) {
      alert('Por favor, adicione pelo menos uma anotação antes de salvar a análise.');
      return;
    }

    // Inicializar estrutura de grupos de olhos
    const eyeGroups = {
      L: { images: [] },
      R: { images: [] }
    };

    // Iterar sobre as imagens processadas para montar a estrutura
    processedImages.forEach((image, index) => {
      // Filtrar anotações para esta imagem
      const imageAnnotations = annotations.filter(
        ann => ann.imageIndex === index
      );

      // Converter para o formato esperado de sinais
      const iridologicalSigns = imageAnnotations.map(ann => {
        // Converter pontos para o formato esperado
        let locationData;

        if (ann.type === 'polygon') {
          locationData = {
            location_polygon: ann.points.map(p => [Math.round(p.x), Math.round(p.y)])
          };
        } else {
          locationData = {
            location_point: [Math.round(ann.points[0].x), Math.round(ann.points[0].y)]
          };
        }

        return {
          sign_id: ann.details.sign_id || `sign_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
          type: ann.details.type,
          ...locationData,
          iridology_zone: ann.details.iridology_zone,
          characteristics: ann.details.characteristics,

          // Campos padrão e novos
          iridological_meaning: ann.details.iridological_meaning || '',
          interpretation: ann.details.interpretation || '',
          related_bodies: ann.details.related_bodies || '',
          potential_gravity: ann.details.potential_gravity || '',
          recommendations: ann.details.recommendations || '',
          clinical_recommendations: ann.details.clinical_recommendations || '',
          expert_interpretation: ann.details.expert_interpretation || '' // Mantendo compatibilidade se necessário
        };
      });

      // Determinar o lado do olho e adicionar ao grupo correto
      const imageObj = {
        image_filename: image.filename,
        image_src: image.src,
        eye_side: image.eye,
        image_quality: imageQualities[index] || 'Boa',
        iris_color: irisColors[index] || '',
        relief_level: "Médio", // Valor padrão ou poderia ser adicionado ao estado
        iridological_signs: iridologicalSigns,
        detailed_summary: "" // Placeholder para sumário detalhado
      };

      if (image.eye === 'L') {
        eyeGroups.L.images.push(imageObj);
      } else if (image.eye === 'R') {
        eyeGroups.R.images.push(imageObj);
      }
    });

    // Preparar os dados no formato JSON esperado
    const annotationsData = {
      annotations: { // Envolvendo em annotations conforme o exemplo do JSON
        eye_groups: eyeGroups,
        overall_analysis: overallAnalysis,
        annotator_id: user?._id || "google/gemma-3-27b-it:free",
        review_status: 'Complete'
      }
    };

    // NOTA: O backend pode esperar 'annotations' como chave raiz ou o conteúdo direto.
    // Baseado no código anterior, 'annotationsData' era passado para onSaveAnnotation.
    // O código anterior passava um objeto que tinha 'images', 'overall_analysis', etc.
    // O JSON solicitado tem 'annotations: { eye_groups: ... }'.
    // Se onSaveAnnotation salva o que é passado dentro do campo 'annotations' do objeto 'call',
    // então devemos passar apenas o conteúdo de 'annotations'.

    const contentToSave = {
      eye_groups: eyeGroups,
      overall_analysis: overallAnalysis,
      annotator_id: user?._id || "google/gemma-3-27b-it:free",
      review_status: 'Complete'
    };

    console.log('annotationsData', contentToSave);

    // Chamar a função de callback com os dados
    onSaveAnnotation(contentToSave);
  };

  // Renderizar a lista de imagens disponíveis
  const renderImageList = () => {
    return (
      <div className="image-list">
        <h3>Imagens do Cliente</h3>
        <div className="image-thumbnails">
          {processedImages.map((image, index) => {
            let eyeLabel = 'Desconhecido';
            if (image.eye === 'L') eyeLabel = 'Olho Esquerdo';
            if (image.eye === 'R') eyeLabel = 'Olho Direito';
            return (
              <div
                key={index}
                className={`image-thumbnail ${currentImageIndex === index ? 'active' : ''}`}
                onClick={() => setCurrentImageIndex(index)}
              >
                <img
                  src={image.src}
                  alt={`Íris ${index + 1}`}
                />
                <span className="eye-label">{eyeLabel}</span>
                <span className="annotation-count">
                  {annotations.filter(ann => ann.imageIndex === index).length} anotações
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAnnotationList = () => {
    return (
      <div className="annotations-list">
        <h3>Anotações ({annotations.filter(ann => ann.imageIndex === currentImageIndex).length})</h3>
        <div className="annotations-scroll">
          {annotations
            .filter(ann => ann.imageIndex === currentImageIndex)
            .map((annotation, index) => (
              <div
                key={annotation.id}
                className={`annotation-item ${currentAnnotation === annotation ? 'active' : ''}`}
                onClick={() => selectAnnotation(annotation)}
              >
                <span className="annotation-number">{index + 1}</span>
                <div className="annotation-info">
                  <span className="annotation-type">
                    {annotation.details.type || 'Sem tipo definido'}
                  </span>
                  <span className="annotation-zone">
                    {annotation.details.iridology_zone || 'Zona não especificada'}
                  </span>
                </div>
                <button
                  className="delete-annotation-button"
                  onClick={deleteAnnotation}
                >
                  <FiTrash2 />
                </button>
                <button
                  className="edit-annotation-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectAnnotation(annotation);
                  }}
                >
                  <FiEdit2 />
                </button>
              </div>
            ))}
        </div>
      </div>
    )
  }

  // Renderizar a área de anotação
  const renderAnnotationArea = () => {
    const currentImage = processedImages[currentImageIndex];

    // Determinar qual mapa de íris usar
    const irisMapUrl = currentImage?.eye === 'L'
      ? 'https://storage.googleapis.com/e-iris-businesses/map-iris-left.gif'
      : 'https://storage.googleapis.com/e-iris-businesses/map-iris-right.gif';

    return (
      <div className="annotation-area">
        <div className="image-quality-section">
          <div className="form-group" style={{ marginBottom: '0px' }}>
            <label htmlFor="image-quality">Qualidade da Foto:</label>
            <select
              id="image-quality"
              value={imageQualities[currentImageIndex] || 'Boa'}
              onChange={(e) => updateImageQuality(e.target.value)}
            >
              {imageQualityOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: '0px' }}>
            <label htmlFor="iris-color">Cor da Íris:</label>
            <select
              id="iris-color"
              value={irisColors[currentImageIndex] || ''}
              onChange={(e) => updateIrisColor(e.target.value)}
            >
              <option value="">Selecione a cor</option>
              {irisColorOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="image-container" ref={imageContainerRef}>
          <div className="annotation-toolbar">
            <button
              className={`tool-button ${drawingMode === 'polygon' ? 'active' : ''}`}
              onClick={() => {
                setDrawingMode('polygon');
                setCurrentPolygon([]);
                setCurrentAnnotation(null);
              }}
              title="Desenhar Polígono"
            >
              <FiSquare />
            </button>
            <button
              className={`tool-button ${drawingMode === 'point' ? 'active' : ''}`}
              onClick={() => {
                setDrawingMode('point');
                setCurrentAnnotation(null);
              }}
              title="Marcar Ponto"
            >
              <FiCircle />
            </button>
            <button
              className="tool-button"
              onClick={() => setShowAnnotations(!showAnnotations)}
              title={showAnnotations ? 'Ocultar Anotações' : 'Mostrar Anotações'}
            >
              {showAnnotations ? <FiEyeOff /> : <FiEye />}
            </button>
            <button
              className={`tool-button ${showIrisMap ? 'active' : ''}`}
              onClick={toggleIrisMap}
              title={showIrisMap ? 'Ocultar Mapa da Íris' : 'Mostrar Mapa da Íris'}
            >
              <FiLayers />
            </button>
            {drawingMode === 'polygon' && currentPolygon.length >= 3 && (
              <button
                className="tool-button finish-button"
                onClick={finishPolygon}
                title="Finalizar Polígono"
              >
                <FiPlus /> Finalizar
              </button>
            )}
            {drawingMode !== 'none' && (
              <button
                className="tool-button cancel-button"
                onClick={cancelDrawing}
                title="Cancelar Desenho"
              >
                <FiTrash2 /> Cancelar
              </button>
            )}
          </div>

          <img
            ref={imageRef}
            src={currentImage?.src}
            alt="Íris para anotação"
            className="annotation-image"
          />
          {/*showIrisMap && (
            <img 
              ref={irisMapRef}
              src={irisMapUrl}
              alt="Mapa da Íris" 
              className="iris-map"
              style={{ opacity: irisMapOpacity }}
            />
          )*/}
          <canvas
            ref={canvasRef}
            className="annotation-canvas"
            style={drawingMode === 'none' ? { cursor: 'default' } : { cursor: 'crosshair' }}
            onClick={handleCanvasClick}
          />
          {showIrisMap && (
            <Rnd
              className="divImgMapIrisView"
              default={{
                x: 250,
                y: 120,
                width: 400,
                height: 400
              }}
            >
              <span className="btnInvertMapColor" onClick={() => setShowIrisMap(false)} title={t('evaluation.iris.controls.closeMap')}>
                <i className="fas fa-times"></i>
              </span>
              <span className="btnInvertMapColor" onClick={() => setInvertIrisMap(!invertIrisMap)} title={t('evaluation.iris.controls.invertMapColor')} style={{ marginTop: 55 }}>
                <i className="fas fa-fill-drip"></i>
              </span>
              <label style={{ backgroundImage: `url("${irisMapUrl}")`, opacity: irisMapOpacity, filter: invertIrisMap ? 'invert(100%)' : 'none' }} className={'imgMapIrisView'}></label>
              <input type="range" min="0" max="1" step="0.1" value={irisMapOpacity} onChange={handleOpacityChange}></input>
            </Rnd>
          )}
        </div>
      </div>
    );
  };

  // Renderizar o formulário de detalhes da anotação
  const renderAnnotationDetails = () => {
    return (
      <div className="annotation-details">
        <h3>Detalhes da Anotação</h3>

        {currentAnnotation ? (
          <div className="annotation-form">

            <div className="form-group">
              <label htmlFor="type">Tipo de Sinal*</label>
              <select
                id="type"
                name="type"
                value={annotationDetails.type}
                onChange={updateAnnotationDetails}
                required
              >
                <option value="">Selecione o tipo</option>
                <option value="Lacuna">Lacuna</option>
                <option value="Pigmento">Pigmento</option>
                <option value="Anel">Anel</option>
                <option value="Raio Solar">Raio Solar</option>
                <option value="Cripta">Cripta (Fissura)</option>
                <option value="Mancha">Mancha</option>
                <option value="Arco">Arco</option>
                <option value="Outro">Outro</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="sign_id">Identificador do Sinal*</label>
              <select
                id="sign_id"
                name="sign_id"
                value={annotationDetails.sign_id}
                onChange={updateAnnotationDetails}
                required
              >
                <option value="">Selecione o identificador</option>
                {annotationDetails.type ?
                  // Mostrar apenas as opções do tipo selecionado
                  signIdOptionsByType[annotationDetails.type]?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                  :
                  // Mostrar todas as opções quando nenhum tipo estiver selecionado
                  signIdOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                }
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="iridology_zone">Zona Iridológica*</label>
              <select
                id="iridology_zone"
                name="iridology_zone"
                value={annotationDetails.iridology_zone}
                onChange={updateAnnotationDetails}
                required
              >
                <option value="">Selecione a zona</option>
                {iridologyZoneOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="characteristics-section">
              <h4>Características do Sinal</h4>

              <div className="form-group">
                <label htmlFor="characteristics.color">Cor</label>
                <select
                  id="characteristics.color"
                  name="characteristics.color"
                  value={annotationDetails.characteristics.color}
                  onChange={updateAnnotationDetails}
                >
                  <option value="">Cor do Sinal</option>
                  {signColorOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="characteristics.size">Tamanho</label>
                <select
                  id="characteristics.size"
                  name="characteristics.size"
                  value={annotationDetails.characteristics.size}
                  onChange={updateAnnotationDetails}
                >
                  <option value="">Selecione o tamanho</option>
                  {signSizeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="characteristics.depth">Profundidade</label>
                <select
                  id="characteristics.depth"
                  name="characteristics.depth"
                  value={annotationDetails.characteristics.depth}
                  onChange={updateAnnotationDetails}
                >
                  <option value="">Selecione a profundidade</option>
                  {signDepthOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="characteristics.shape">Forma</label>
                <select
                  id="characteristics.shape"
                  name="characteristics.shape"
                  value={annotationDetails.characteristics.shape}
                  onChange={updateAnnotationDetails}
                >
                  <option value="">Forma do Sinal</option>
                  {signShapeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="expert_interpretation">Interpretação*</label>
              <textarea
                id="expert_interpretation"
                name="expert_interpretation"
                value={annotationDetails.expert_interpretation}
                onChange={updateAnnotationDetails}
                placeholder="Descreva a interpretação iridológica deste sinal..."
                rows={4}
                required
              />
            </div>

            <div className="annotation-actions">
              <button
                className="delete-button"
                onClick={deleteAnnotation}
              >
                <FiTrash2 /> Excluir Anotação
              </button>
              <button
                className="save-button"
                onClick={saveAnnotationDetails}
              >
                <FiSave /> Salvar
              </button>
            </div>
          </div>
        ) : (
          <div className="no-annotation-selected">
            <p>Selecione uma anotação existente ou crie uma nova para editar os detalhes.</p>
          </div>
        )}
      </div>
    );
  };

  // Renderizar a seção de análise geral
  const renderOverallAnalysis = () => {
    return (
      <div className="overall-analysis">
        <h3>Análise Geral</h3>

        <div className="form-group">
          <label htmlFor="overall_constitution">Constituição Geral</label>
          <select
            id="overall_constitution"
            name="overall_constitution"
            value={overallAnalysis.overall_constitution}
            onChange={updateOverallAnalysis}
          >
            <option value="">Selecione a constituição</option>
            {constitutionOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="fibers">Constituição das Fibras</label>
          <select
            id="fibers"
            name="fibers"
            value={overallAnalysis.fibers}
            onChange={updateOverallAnalysis}
          >
            <option value="">Selecione a constituição das Fibras</option>
            {constitutionFibersOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="general_observations">Observações Gerais</label>
          <textarea
            id="general_observations"
            name="general_observations"
            value={overallAnalysis.general_observations}
            onChange={updateOverallAnalysis}
            placeholder="Observações gerais sobre a análise iridológica..."
            rows={4}
          />
        </div>

        <div className="form-group">
          <label htmlFor="recommendations">Recomendações</label>
          <textarea
            id="recommendations"
            name="recommendations"
            value={overallAnalysis.recommendations}
            onChange={updateOverallAnalysis}
            placeholder="Recomendações para o cliente com base na análise..."
            rows={4}
          />
        </div>

        {!call?.analysis?.report_html &&
          <button
            className="save-analysis-button"
            onClick={saveAnnotation}
          >
            <FiSave /> Salvar Análise Completa
          </button>
        }

        {call?.analysis?.access_key &&
          <button
            className="view-report-button"
            style={{ width: '100%', background: 'var(--secondary-color)' }}
            onClick={() => window.open(`/analysis-report/${call?._id}?access-key=${call?.analysis?.access_key}`, '_blank')}
          >
            <FiFileText /> Ver Relatório Gerado
          </button>
        }
      </div>
    );
  };

  // Renderizar informações do cliente
  const renderClientInfo = () => {
    if (!clientData) return null;

    return (
      <div className="client-info">
        <h3>Informações do Cliente</h3>
        <div className="client-details">
          <p><strong>Nome:</strong> {clientData.name}</p>
          <p><strong>E-mail:</strong> {clientData.email}</p>
          <span onClick={() => onSendWhatsApp(clientData)} style={{ cursor: 'pointer' }}>
            <p><strong>Telefone:</strong> {clientData.phone} <FaWhatsapp size={22} color="#25D366" style={{marginTop: -5}}/></p>
          </span>
          <p><strong>Data de Nascimento:</strong> {clientData.birthDate}</p>
          <p><strong>Pagamento:</strong> {call.payment?.status === 'paid' ? <span className="payment-status paid">Pago</span> : <span className="payment-status pending">Pendente</span>}</p>
        </div>
        {call?.analysis && 
          <div className="client-actions">
            {!call?.analysis?.send_email ?
              <button
                className="send-email-button"
                onClick={onSendEmail}
              >
                <FiSend /> Enviar Análise para o e-mail
              </button>
              :
              <span className="send-email-confirmed">
                <strong><FiSend /> Análise já enviada para o e-mail!</strong>
              </span>}
          </div>
        }
      </div>
    );
  };

  return (
    <div className="annotation-tool-container">
      <div className="annotation-header">
        {renderClientInfo()}
      </div>

      <div className="annotation-content">
        <div className="left-panel">
          {renderImageList()}
          {renderAnnotationList()}
          {renderOverallAnalysis()}
        </div>

        <div className="center-panel">
          {renderAnnotationArea()}
        </div>

        <div className="right-panel">
          {renderAnnotationDetails()}
        </div>
      </div>
    </div>
  );
};

export default AnnotationTool;
